import { defineStore } from 'pinia'
import {
  ORDER_TABS,
  TabNames,
} from '@modules/app-steps/step-two/tabs/tabs-data'
import { activeStep } from '@/components/modules/app-steps/step-two/index.state'
import { isClientCreditsExists } from '@core/composables/useClientCredits'

import { useOrderStore } from './order'
import { OrderTypes } from '../components/modules/app-steps/step-two/tabs/tabs-data'
import {
  IS_MAIN_STORAGE_KEY,
  SPECIAL_PACKAGE_STORAGE_KEY,
} from '@modules/app-steps/step-one/useSpecialPackage'

const restrictCreditsTab = (tab) => {
  if (tab.name === TabNames.Credits && !isClientCreditsExists.value)
    return false
  return true
}

const useTabsStore = defineStore('tabs', {
  state: () => {
    return {
      tabs: ORDER_TABS,
    }
  },

  actions: {
    hideTab(tabName) {
      const index = this.tabsToShow.findIndex((tab) => tab.name === tabName)
      this.tabsToShow.splice(index, 1)
    },

    backTabsToInitialState() {
      this.tabs = []
    },
  },

  getters: {
    tabsToShow({ tabs }) {
      const isSpecial = localStorage.getItem(IS_MAIN_STORAGE_KEY) === 'no'
      const specialPackage = JSON.parse(
        localStorage.getItem(SPECIAL_PACKAGE_STORAGE_KEY)
      )
      const orderStore = useOrderStore()

      const orderType = orderStore?.orderType ?? OrderTypes.UnSelected
      const selectedOrderType = orderStore.selectedOrderType

      const targetOrderType = selectedOrderType || orderType
      const tabsForOrderType = tabs[targetOrderType] ?? []

      if (
        orderType === OrderTypes.RedeemedCredit &&
        selectedOrderType === OrderTypes.Normal
      ) {
        return tabs[OrderTypes.RedeemedCredit][0].tabs[OrderTypes.Normal]
      }

      if (
        orderType === OrderTypes.RedeemedCredit &&
        selectedOrderType === OrderTypes.WritingService
      ) {
        return tabs[OrderTypes.RedeemedCredit][0].tabs[
          OrderTypes.WritingService
        ]
      }

      if (orderType === OrderTypes.RedeemedCredit && !orderStore.isWriting) {
        const defaultTabs = tabs[orderType]
        const selectedTypeTabs =
          defaultTabs[0].tabs?.[orderStore.selectedOrderType] ?? defaultTabs

        return selectedTypeTabs
      }
      const filteredTabs = tabsForOrderType.filter(restrictCreditsTab)
      if (specialPackage?.creditOnly && isSpecial) {
        const prepayTab = filteredTabs.find(
          (tab) => tab.name === TabNames.Prepay
        )

        if (!prepayTab) return filteredTabs

        return [prepayTab]
      }

      if (isSpecial) {
        const tabs = filteredTabs.filter(
          (tab) => tab.name !== TabNames.Packages
        )

        return tabs
      }

      return filteredTabs
    },
    hasClientCredits: () => isClientCreditsExists,
    // activeStep is restored from the order's persisted metadata and can outlive
    // a shrinking tab set (e.g. once an order converts to a single-tab flow such
    // as credit-only or credit-extension). Clamp it into range so tab lookups
    // never fall off the end and surface as a dead "Next - undefined" button.
    currentStep: ({ tabsToShow }) =>
      Math.min(Math.max(activeStep.value, 0), Math.max(tabsToShow.length - 1, 0)),
    activeTab: ({ tabsToShow, currentStep }) => {
      return tabsToShow[currentStep] ?? tabsToShow[0]
    },
    nextTab: ({ tabsToShow, currentStep }) => {
      return tabsToShow[currentStep + 1] ?? ''
    },
    isLastTab: ({ tabsToShow, currentStep }) => tabsToShow.length - 1 === currentStep,
    lastTabIndex: ({ tabsToShow }) => tabsToShow.length - 1,
    previousOpenedTab: () => activeStep.value - 1,
    nextOpenedTab: ({ isLastTab }) => {
      if (isLastTab) return BILLING_PAGE_TAB_VALUE
      return activeStep.value + 1
    },
    getFrontendTabBuilder:
      ({ previousOpenedTab, nextOpenedTab }) =>
      (isNext) => {
        const previousTabProps = {
          metadata: {
            currentTab: previousOpenedTab,
          },
        }

        const nextTabProps = {
          metadata: {
            currentTab: nextOpenedTab,
            maxOpenedTab: nextOpenedTab,
          },
        }

        return isNext ? nextTabProps : previousTabProps
      },
  },
})

const BILLING_PAGE_TAB_VALUE = -1

export { useTabsStore, BILLING_PAGE_TAB_VALUE }
